import React, { useEffect, useRef } from 'react';
import { formatDublinDateTime } from '../../utils/date';
import { Link, useNavigate } from 'react-router-dom';
import { ChatBubbleLeftRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { MessageDelivery, MessageDeliveryStatus, MessageType, UserType } from '../../types';
import { useMarkMessageReadMutation } from '../../store/api/localApi';
import MessageReplyBox from './MessageReplyBox';

interface MessageDetailModalProps {
  delivery: MessageDelivery;
  open: boolean;
  onClose: () => void;
  /** Every conversation the family already has about this send, one per
   *  child (from the family inbox). */
  repliedThreads?: Array<{ studentId: string; childName: string; threadId: string }>;
}

const threadPath = (threadId: string) => `/parent/family/threads/${threadId}`;

function formatSentAt(iso: string): string {
  try {
    return formatDublinDateTime(iso);
  } catch {
    return iso;
  }
}

function roleBadgeLabel(role: UserType): string {
  switch (role) {
    case UserType.PARENT:
      return 'Parent';
    case UserType.STAFF:
      return 'Staff';
    case UserType.LOCATION_ADMIN:
      return 'Location Admin';
    case UserType.ORG_ADMIN:
      return 'Admin';
    case UserType.SUPER_ADMIN:
      return 'Super Admin';
    default:
      return role;
  }
}

const MessageDetailModal: React.FC<MessageDetailModalProps> = ({ delivery, open, onClose, repliedThreads }) => {
  const [markMessageRead] = useMarkMessageReadMutation();
  const navigate = useNavigate();
  // Track which deliveries we've already fired the read mutation for so we
  // don't re-fire if the parent re-renders while the modal stays open.
  const markedRef = useRef<Set<string>>(new Set());
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (
      open &&
      delivery.status === MessageDeliveryStatus.UNREAD &&
      !markedRef.current.has(delivery.id)
    ) {
      markedRef.current.add(delivery.id);
      markMessageRead(delivery.id);
    }
  }, [open, delivery.id, delivery.status, markMessageRead]);

  // Escape-to-close + initial focus to the close button so keyboard users
  // can dismiss without reaching for the mouse and screen readers land on
  // a meaningful control instead of the backdrop.
  useEffect(() => {
    if (!open) return;
    closeButtonRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const isParentDm = delivery.type === MessageType.PARENT_DM;
  // Newer deliveries carry the full text; older rows only ever had the
  // 140-character preview, and for those the honest thing is still to say so.
  const bodyText = delivery.body ?? delivery.bodyPreview;
  const bodyIsTruncated = delivery.body === undefined && delivery.bodyPreview.length === 140;
  // repliedThreads (from the family inbox) is authoritative: it names every child.
  // The delivery's own threadId is only the LATEST reply (attachThread overwrites
  // it) and announcements carry no studentName, so it is a fallback, not the source.
  // A threadId without a studentId names no child, so it adds no link.
  const fromInbox = repliedThreads ?? [];
  const own =
    delivery.threadId && delivery.studentId && !fromInbox.some((r) => r.threadId === delivery.threadId)
      ? [
          {
            studentId: delivery.studentId,
            childName: delivery.studentName?.split(' ')[0] ?? 'your child',
            threadId: delivery.threadId,
          },
        ]
      : [];
  const links = [...fromInbox, ...own];

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      data-testid="message-detail-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="message-detail-title"
    >
      <div className="flex items-center justify-center min-h-screen px-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75"
          onClick={onClose}
          aria-hidden="true"
        />

        {/* Panel */}
        <div className="relative z-10 bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
          {/* Header row */}
          <div className="flex items-start justify-between mb-4">
            <h3 id="message-detail-title" className="text-lg font-semibold text-gray-900 pr-4">
              {delivery.title}
            </h3>
            <button
              ref={closeButtonRef}
              type="button"
              onClick={onClose}
              data-testid="message-detail-close"
              className="flex-shrink-0 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
              aria-label="Close message"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Sender metadata */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="text-sm text-gray-700 font-medium">{delivery.senderName}</span>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
              {roleBadgeLabel(delivery.senderRole)}
            </span>
            {isParentDm && delivery.studentName && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                About {delivery.studentName}
              </span>
            )}
          </div>

          {/* Body — the full text when the delivery carries it. Rows from
              before that only hold the 140-char preview and say so, since a
              parent has no endpoint to fetch the Message itself. */}
          <p data-testid="message-detail-body" className="text-sm text-gray-700 whitespace-pre-wrap mb-2">
            {bodyText}
          </p>
          {bodyIsTruncated && (
            <p className="text-xs text-gray-400 italic mb-6" data-testid="message-detail-truncated">
              Showing preview only.
            </p>
          )}
          {!bodyIsTruncated && <div className="mb-6" />}

          {/* Thread deep-links — one per conversation behind this row: the
              thread a staff reply belongs to, or each child a send has
              already been answered about. Announcements with no reply yet
              have none. */}
          {links.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-6">
              {links.map((l) => (
                <Link
                  key={l.threadId}
                  to={threadPath(l.threadId)}
                  onClick={onClose}
                  data-testid={`thread-delivery-link-${l.threadId}`}
                  className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-3.5 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                >
                  <ChatBubbleLeftRightIcon className="h-4 w-4" aria-hidden="true" />
                  {links.length === 1 ? 'Open conversation' : `Open conversation about ${l.childName}`}
                </Link>
              ))}
            </div>
          )}

          {/* Reply box. Sending one invalidates MessageDelivery, so the row
              re-fetches with its threadId and the link above appears. Once a
              conversation exists the box leaves out every child it is about and
              renders nothing unless another child is in scope, so a parent of
              two can still answer about the second. */}
          {delivery.repliesAllowed && (
            <MessageReplyBox
              delivery={delivery}
              alreadyRepliedStudentIds={links.map((l) => l.studentId)}
              onSent={(threadId) => {
                onClose();
                navigate(threadPath(threadId));
              }}
            />
          )}

          {/* Footer: timestamp */}
          <p className="text-xs text-gray-400">{formatSentAt(delivery.createdAt)}</p>
        </div>
      </div>
    </div>
  );
};

export default MessageDetailModal;
